import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { CalendarDays, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { GestorDashboardDateRangeCalendar } from '@/components/Gestores/GestorDashboardDateRangeCalendar'
import { parseIsoDate } from '@/components/Gestores/gestorDashboardDateRangeUtils'
import { csLabelClass } from '@/components/Cs/csFormStyles'

type Props = {
  label: string
  value: string | null
  onChange: (isoDateOrNull: string | null) => void
  placeholder?: string
}

function toIsoDateOnly(iso: string | null) {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

function formatPtBr(isoDate: string) {
  if (!isoDate) return ''
  const date = parseIsoDate(isoDate)
  return date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function CsDateField({
  label,
  value,
  onChange,
  placeholder = 'Selecionar data',
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const labelId = useId()
  const isoValue = toIsoDateOnly(value)
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(() => (isoValue ? parseIsoDate(isoValue).getFullYear() : new Date().getFullYear()))
  const [viewMonth, setViewMonth] = useState(() => (isoValue ? parseIsoDate(isoValue).getMonth() : new Date().getMonth()))

  useEffect(() => {
    if (!open) return
    if (isoValue) {
      const anchor = parseIsoDate(isoValue)
      setViewYear(anchor.getFullYear())
      setViewMonth(anchor.getMonth())
    }
  }, [open, isoValue])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  const display = useMemo(() => (isoValue ? formatPtBr(isoValue) : ''), [isoValue])

  return (
    <div ref={rootRef} className='relative mb-3 last:mb-0'>
      <span id={labelId} className={csLabelClass}>
        {label}
      </span>

      <div className='flex items-center gap-1'>
        <button
          type='button'
          aria-labelledby={labelId}
          aria-expanded={open}
          aria-haspopup='dialog'
          onClick={() => setOpen((prev) => !prev)}
          className={cn(
            'flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 text-left text-sm transition-colors',
            open
              ? 'border-accent/40 text-[var(--text-primary)] ring-1 ring-accent/20'
              : 'text-[var(--text-primary)] hover:border-accent/30',
          )}
        >
          <CalendarDays size={15} className='shrink-0 text-[var(--text-muted)]' aria-hidden />
          <span className={cn('min-w-0 truncate', !display && 'text-[var(--text-muted)]')}>
            {display || placeholder}
          </span>
        </button>

        {isoValue && (
          <button
            type='button'
            onClick={() => onChange(null)}
            className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[var(--glass-border)] text-[var(--text-muted)] transition-colors hover:bg-white/[0.04] hover:text-[var(--text-primary)]'
            aria-label={`Limpar ${label.toLowerCase()}`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <>
          <div className='fixed inset-0 z-40 bg-black/40 md:hidden' aria-hidden onClick={() => setOpen(false)} />
          <div
            role='dialog'
            aria-label={label}
            className={cn(
              'z-50 w-[min(100vw-2rem,20rem)] rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-3 shadow-2xl',
              'fixed inset-x-4 bottom-4 md:absolute md:inset-auto md:left-0 md:top-full md:mt-2',
            )}
          >
            <GestorDashboardDateRangeCalendar
              viewYear={viewYear}
              viewMonth={viewMonth}
              draftFrom={isoValue}
              draftTo={isoValue}
              onMonthChange={(year, month) => {
                setViewYear(year)
                setViewMonth(month)
              }}
              onDaySelect={(iso) => {
                onChange(new Date(`${iso}T12:00:00.000Z`).toISOString())
                setOpen(false)
              }}
            />
            <div className='mt-3 flex justify-between border-t border-[var(--glass-border)] pt-3'>
              <button
                type='button'
                className='text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                onClick={() => {
                  const today = new Date()
                  const iso = today.toISOString().slice(0, 10)
                  onChange(new Date(`${iso}T12:00:00.000Z`).toISOString())
                  setOpen(false)
                }}
              >
                Hoje
              </button>
              <button
                type='button'
                className='text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                onClick={() => setOpen(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
